import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, QrCode, Trash2, Pencil, PawPrint, X, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Section, Sheet } from "@/components/orio";
import { motion } from "framer-motion";

export default function MyDogs() {
  const [dogs, setDogs] = useState([]);
  const [qrFor, setQrFor] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/dogs");
    setDogs(data);
  };
  useEffect(() => { load(); }, []);

  const openQr = async (id) => {
    setQrFor(id);
    const { data } = await api.get(`/dogs/${id}/qr`);
    setQrUrl(data);
  };

  const del = async (id) => {
    if (!confirm("Eliminare questo passaporto?")) return;
    await api.delete(`/dogs/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 pb-32">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-2 inline-flex items-center gap-1.5">
            <PawPrint className="h-3 w-3" /> Passaporti digitali
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
            I <span className="orio-grad-text">miei cani</span>
          </h1>
          <p className="text-[#9A9A9A] text-sm mt-1.5 max-w-lg">
            Ogni cane ha il suo passaporto, il suo QR pubblico e la sua memoria.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/app/cassetto"
            data-testid="open-cassetto-btn"
            className="px-3.5 py-2.5 rounded-full bg-white border border-[#F0EBE3] text-[#1A1A1A] font-medium text-sm hover:bg-[#F4EFE6] transition-all inline-flex items-center gap-1.5"
            title="Apri Cassetto digitale"
          >
            <BookOpen className="h-4 w-4" /> <span className="hidden sm:inline">Cassetto</span>
          </Link>
          <Link
            to="/app/cani/nuovo"
            data-testid="add-dog-btn"
            className="px-4 py-2.5 rounded-full orio-cta-btn text-white font-semibold text-sm transition-all inline-flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nuovo</span>
          </Link>
        </div>
      </div>

      {dogs.length === 0 ? (
        <div className="rounded-3xl border border-[#F0EBE3] bg-white p-10 sm:p-14 text-center overflow-hidden relative shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]">
          <div aria-hidden className="absolute inset-0 -z-0 opacity-30"
            style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(45,186,96,0.10) 0%, rgba(255,255,255,0) 70%)" }} />
          <div className="relative">
            <div className="h-16 w-16 rounded-3xl bg-[#E0F5E7] flex items-center justify-center mx-auto mb-4">
              <PawPrint className="h-7 w-7 text-[#2DBA60]" strokeWidth={1.8} />
            </div>
            <div className="font-display text-2xl text-[#1A1A1A]">Nessun passaporto ancora</div>
            <p className="text-[#6B6B6B] text-sm mt-2 max-w-md mx-auto leading-relaxed">
              Crea il primo passaporto ORIO del tuo cane: foto, microchip, libretto e QR pubblico in 1 minuto.
            </p>
            <Link
              to="/app/cani/nuovo"
              data-testid="empty-add-dog"
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full orio-cta-btn text-sm font-semibold"
            >
              <Plus className="h-4 w-4" /> Crea il primo passaporto
            </Link>
          </div>
        </div>
      ) : (
        <Section>
          {/* Mobile: horizontal swipe carousel — Apple Wallet style.
             Uses a native horizontal scroll (not Shadcn Carousel) so touch swipe
             is reliable on iOS/Android PWAs even with nested <Link>s. */}
          <div className="sm:hidden -mx-4">
            <div
              data-testid="dogs-carousel"
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2 px-4"
              style={{ scrollPaddingInline: "1rem", WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
            >
              {dogs.map((d) => (
                <div key={d.id} className="snap-start shrink-0 w-[78vw] max-w-[320px]">
                  <DogCard
                    d={d}
                    width="w-full"
                    onQR={() => openQr(d.id)}
                    onDelete={() => del(d.id)}
                    navigate={navigate}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Desktop: grid */}
          <motion.div
            className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {dogs.map((d) => (
              <motion.div
                key={d.id}
                variants={{
                  hidden: { opacity: 0, y: 14 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.40, ease: [0.22, 0.61, 0.36, 1] } },
                }}
              >
                <DogCard d={d} onQR={() => openQr(d.id)} onDelete={() => del(d.id)} navigate={navigate} />
              </motion.div>
            ))}
          </motion.div>
        </Section>
      )}

      {/* QR bottom sheet */}
      <Sheet
        open={!!qrFor && !!qrUrl}
        onClose={() => { setQrFor(null); setQrUrl(null); }}
        eyebrow="Passaporto pubblico"
        title="Scansiona o condividi"
        testid="qr-sheet"
        footer={
          <button
            onClick={() => { setQrFor(null); setQrUrl(null); }}
            className="w-full px-4 py-3 rounded-full bg-white text-black font-semibold text-sm"
          >
            Chiudi
          </button>
        }
      >
        {qrUrl && (
          <>
            <div className="rounded-2xl p-5 bg-white flex items-center justify-center">
              <img src={qrUrl.qr_data_url} alt="QR passaporto" className="max-w-full w-64 h-64 object-contain" />
            </div>
            <div className="text-[11px] text-[#9A9A9A] mt-3 break-all font-mono leading-relaxed">
              {qrUrl.public_url}
            </div>
            <div className="text-[12px] text-[#9A9A9A] mt-3">
              Chiunque scansiona questo QR vede i dati pubblici del passaporto: contatto in caso di smarrimento, microchip e taglia. Tu controlli cosa rendere visibile.
            </div>
          </>
        )}
      </Sheet>
    </div>
  );
}

/* ----------- DogCard (used in mobile carousel + desktop grid) ----------- */
function DogCard({ d, width = "", onQR, onDelete, navigate }) {
  return (
    <div
      data-testid={`dog-card-${d.id}`}
      className={`group rounded-3xl overflow-hidden border border-[#F0EBE3] relative bg-white ${width}`}
      style={{
        boxShadow: "0 10px 30px -12px rgba(0,0,0,0.10), 0 2px 6px -2px rgba(0,0,0,0.04)",
      }}
    >
      <Link to={`/app/cani/${d.id}`} className="relative block aspect-[4/5] overflow-hidden">
        {d.photo_url ? (
          <img src={d.photo_url} alt={d.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ background: "radial-gradient(60% 60% at 50% 50%, rgba(255,176,32,0.18) 0%, rgba(0,0,0,0) 70%)" }}>
            <div className="font-display text-7xl text-[#1A1A1A]/25">
              {(d.name || "?").slice(0, 1).toUpperCase()}
            </div>
          </div>
        )}
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)" }} />
        {d.is_stub && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#FFB020] text-black text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Da completare
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 min-w-0">
          <div className="font-display text-2xl leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] truncate">{d.name}</div>
          <div className="text-[12px] text-white/90 mt-0.5 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)] truncate">{d.breed || "Razza non specificata"}</div>
        </div>
      </Link>
      <div className="px-3 py-3 flex gap-2 border-t border-[#F0EBE3] bg-white">
        <button
          data-testid={`edit-dog-${d.id}`}
          onClick={() => navigate(`/app/cani/${d.id}`)}
          className="flex-1 px-3 py-2 rounded-full bg-[#FAF7F0] hover:bg-[#F4EFE6] active:scale-95 text-[12px] font-medium text-[#1A1A1A] flex items-center justify-center gap-1.5 transition-all border border-[#F0EBE3]"
        >
          <Pencil className="h-3.5 w-3.5" /> Modifica
        </button>
        <button
          data-testid={`qr-dog-${d.id}`}
          disabled={d.is_stub}
          onClick={onQR}
          className="flex-1 px-3 py-2 rounded-full bg-[#FAF7F0] hover:bg-[#F4EFE6] active:scale-95 text-[12px] font-medium text-[#1A1A1A] flex items-center justify-center gap-1.5 disabled:opacity-30 transition-all border border-[#F0EBE3]"
        >
          <QrCode className="h-3.5 w-3.5" /> QR
        </button>
        <button
          data-testid={`delete-dog-${d.id}`}
          onClick={onDelete}
          className="h-9 w-9 rounded-full bg-[#FAF7F0] hover:bg-[#FEE5E5] hover:text-[#E94545] text-[#6B6B6B] active:scale-90 flex items-center justify-center transition-all border border-[#F0EBE3]"
          aria-label="Elimina"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
